import { observable, action } from 'mobx';
import util from 'preact-util';

import activityTracker from '../lib/activityTracker';
import PubSub, { topics } from '../lib/pubsub';

const HARD_LOGOUT_KEY = 'lockScreenHardLogoutAt';
const LOCKED_KEY = 'lockScreenIsLocked';
const TICK_INTERVAL_MS = 15 * 1000;

class LockScreenStore {
    @observable isLocked = false;
    @observable roster = [];
    @observable featureEnabled = false;
    @observable softLockMinutes = 15;
    @observable hardLogoutHours = 12;

    @observable selectedUserId = null;
    @observable pinError = null;
    @observable pinLockedUntilByUser = {};

    @observable rosterLoading = false;

    constructor() {
        this.tickHandle = null;
    }

    @action setupTimers() {
        this.teardownTimers();
        activityTracker.install();
        this.tickHandle = setInterval(() => this.tick(), TICK_INTERVAL_MS);
        this.tick();
    }

    @action teardownTimers() {
        if (this.tickHandle) {
            clearInterval(this.tickHandle);
            this.tickHandle = null;
        }
    }

    @action updateConfig({ featureEnabled, softLockMinutes, hardLogoutHours }) {
        if (typeof featureEnabled === 'boolean') this.featureEnabled = featureEnabled;
        if (typeof softLockMinutes === 'number' && softLockMinutes > 0) this.softLockMinutes = softLockMinutes;
        if (typeof hardLogoutHours === 'number' && hardLogoutHours > 0) this.hardLogoutHours = hardLogoutHours;
    }

    tick() {
        if (!this.featureEnabled) return;
        if (this.isLocked) return;

        const idleMs = Date.now() - activityTracker.getLastActivity();
        if (idleMs >= this.softLockMinutes * 60 * 1000) {
            this.lock();
        }

        const hardLogoutAt = Number(localStorage.getItem(HARD_LOGOUT_KEY)) || 0;
        if (hardLogoutAt && Date.now() >= hardLogoutAt) {
            PubSub.publish(topics.LOG_OUT);
        }
    }

    @action lock() {
        if (this.isLocked) return;
        this.isLocked = true;
        this.selectedUserId = null;
        this.pinError = null;
        try { localStorage.setItem(LOCKED_KEY, '1'); } catch (err) { /* ignore */ }
        activityTracker.pause();
        this.loadRoster().catch(err => console.warn('lockScreen.loadRoster failed', err));
    }

    @action rehydrate() {
        // Called on app boot — if the device was locked when the page reloaded,
        // re-apply the lock immediately before the underlying app paints.
        try {
            if (localStorage.getItem(LOCKED_KEY) === '1') {
                this.isLocked = true;
                this.selectedUserId = null;
                this.pinError = null;
                activityTracker.pause();
                this.loadRoster().catch(err => console.warn('lockScreen.loadRoster failed', err));
            }
        } catch (err) {
            /* ignore */
        }
    }

    @action async loadRoster() {
        this.updateKeyValue('rosterLoading', true);
        try {
            const response = await util.fetchApi('/api/users/customer-roster', { method: 'GET' }, {});
            if (response?.data?.roster) {
                this.updateKeyValue('roster', response.data.roster);
                this.updateConfig({
                    featureEnabled: !!response.data.featureEnabled,
                    softLockMinutes: response.data.softLockMinutes,
                    hardLogoutHours: response.data.hardLogoutHours,
                });
            }
        } finally {
            this.updateKeyValue('rosterLoading', false);
        }
    }

    @action updateKeyValue(key, value) {
        this[key] = value;
    }

    @action selectUser(userId) {
        this.selectedUserId = userId;
        this.pinError = null;
    }

    @action async unlock(userId, pin) {
        this.updateKeyValue('pinError', null);
        try {
            const response = await util.fetchApi('/api/users/login-pin', { method: 'POST' }, { userId, pin });
            if (response?.status === 200 && response?.data?.apiToken) {
                util.setJwtToken(response.data.apiToken);
                if (response.data.refreshToken) {
                    localStorage.setItem('refreshToken', response.data.refreshToken);
                }
                if (response.data.deviceSessionStartedAt) {
                    const deadline = response.data.deviceSessionStartedAt + this.hardLogoutHours * 60 * 60 * 1000;
                    localStorage.setItem(HARD_LOGOUT_KEY, String(deadline));
                }
                try { localStorage.removeItem(LOCKED_KEY); } catch (err) { /* ignore */ }
                PubSub.publish(topics.JWT_TOKEN_CHANGED, response.data.apiToken);
                setTimeout(() => window.location.reload(true), 100);
                return { ok: true };
            }
            this.updateKeyValue('pinError', response?.reason || 'wrongPin');
            if (response?.until) {
                this.updateKeyValue('pinLockedUntilByUser', { ...this.pinLockedUntilByUser, [userId]: response.until });
            }
            return { ok: false, reason: this.pinError };
        } catch (err) {
            this.updateKeyValue('pinError', 'networkError');
            return { ok: false, reason: 'networkError' };
        }
    }

    @action recordFullLogin(deviceSessionStartedAt) {
        if (!deviceSessionStartedAt) return;
        const deadline = deviceSessionStartedAt + this.hardLogoutHours * 60 * 60 * 1000;
        localStorage.setItem(HARD_LOGOUT_KEY, String(deadline));
    }

    @action clearOnLogout() {
        this.isLocked = false;
        this.roster = [];
        this.selectedUserId = null;
        this.pinError = null;
        localStorage.removeItem(HARD_LOGOUT_KEY);
        try { localStorage.removeItem(LOCKED_KEY); } catch (err) { /* ignore */ }
        this.teardownTimers();
        activityTracker.uninstall();
    }
}

const lockScreenStore = new LockScreenStore();
export default lockScreenStore;
